"use client";

import React, { useEffect } from "react";
import { Building2, Calendar, Clock, ExternalLink, HardHat, MapPin, Phone, ShieldCheck, ArrowLeft, Briefcase, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;
const getWvcClient = () =>
  typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;

function SingleProjectConsumer() {
  const { posts, loading, error, refetch } = usePosts();
  const post = posts?.[0];

  const custom = post?.customFields || {};
  const projectTitle = custom.project_title || post?.title || "Scaffolding Project";
  const category = custom.category || "Scaffolding";
  const summary = custom.summary || post?.excerpt || "";
  const sector = custom.sector || "";
  const service = custom.service || "";
  const location = custom.location || "";
  const client = custom.client || "";
  const duration = custom.duration || "";
  const requirement = custom.requirement || "";
  const solution = custom.solution || "";
  const relatedServiceLink = custom.related_service_link || "/ScaffoldingServicesPage";

  // Gallery items assembly
  const mainImgUrl = custom.image || post?.featuredImage || "";
  const galleryImg2 = custom.gallery_image_2 || "";
  const galleryImg3 = custom.gallery_image_3 || "";

  const galleryImages: Array<{ src: string; srcset: string; sizes: string; alt: string }> = [];
  if (mainImgUrl) {
    galleryImages.push({
      src: mainImgUrl,
      srcset: "",
      sizes: "",
      alt: `${projectTitle} - Main View`,
    });
  }
  if (galleryImg2) {
    galleryImages.push({
      src: galleryImg2,
      srcset: "",
      sizes: "",
      alt: `${projectTitle} - Detail View 1`,
    });
  }
  if (galleryImg3) {
    galleryImages.push({
      src: galleryImg3,
      srcset: "",
      sizes: "",
      alt: `${projectTitle} - Detail View 2`,
    });
  }

  // Set browser title and meta description dynamically
  useEffect(() => {
    if (projectTitle && typeof document !== "undefined") {
      document.title = `${projectTitle} | BMF Scaffolding Ltd`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc && summary) {
        metaDesc.setAttribute("content", summary.slice(0, 160));
      }
    }
  }, [projectTitle, summary]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 space-y-8 animate-pulse">
        <div className="h-6 w-32 bg-muted rounded-md" />
        <div className="h-12 w-3/4 bg-muted rounded-md" />
        <div className="h-96 w-full bg-muted rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="h-48 bg-muted rounded-xl" />
          <div className="h-48 bg-muted rounded-xl" />
          <div className="h-48 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="text-destructive font-medium mb-4">Unable to load project details.</p>
        <Button onClick={() => refetch()} variant="outline">
          Try again
        </Button>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold font-default text-foreground mb-4">Project not found</h2>
        <p className="text-muted-foreground mb-6">
          The requested scaffolding case study or project could not be located.
        </p>
        <Button asChild variant="default">
          <Link to="/ProjectsCaseStudiesPage">View all projects</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      {/* Back button & Category */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground -ml-2">
          <Link to="/ProjectsCaseStudiesPage">
            <ArrowLeft className="size-4 mr-1.5" />
            Back to projects
          </Link>
        </Button>
        {category && (
          <Badge variant="secondary" className="text-xs uppercase tracking-wide px-3 py-1 font-semibold">
            {category}
          </Badge>
        )}
      </div>

      {/* Hero Content */}
      <header className="mb-10 max-w-4xl">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight mb-4">
          {projectTitle}
        </h1>
        {summary && (
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
            {summary}
          </p>
        )}
      </header>

      {/* Main Project Hero Image */}
      {mainImgUrl && (
        <div className="relative mb-12 rounded-xl overflow-hidden border border-border bg-muted shadow-sm aspect-16/9 max-h-[540px]">
          <img
            src={mainImgUrl}
            data-wvc-srcset=""
            data-wvc-sizes=""
            alt={projectTitle}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {/* Project Overview Stats / Meta Strip */}
      <div className="mb-12 bg-card border border-border rounded-xl p-6 sm:p-8 shadow-xs">
        <h2 className="text-lg font-semibold font-default text-card-foreground mb-6 flex items-center gap-2">
          <Briefcase className="size-5 text-primary" />
          Project overview
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {sector && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Sector</p>
              <p className="text-sm sm:text-base font-semibold text-card-foreground">{sector}</p>
            </div>
          )}
          {service && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Service</p>
              <p className="text-sm sm:text-base font-semibold text-card-foreground">{service}</p>
            </div>
          )}
          {location && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                <MapPin className="size-3 text-muted-foreground" /> Location
              </p>
              <p className="text-sm sm:text-base font-semibold text-card-foreground">{location}</p>
            </div>
          )}
          {client && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                <Building2 className="size-3 text-muted-foreground" /> Client
              </p>
              <p className="text-sm sm:text-base font-semibold text-card-foreground">{client}</p>
            </div>
          )}
          {duration && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                <Clock className="size-3 text-muted-foreground" /> Duration
              </p>
              <p className="text-sm sm:text-base font-semibold text-card-foreground">{duration}</p>
            </div>
          )}
        </div>
      </div>

      {/* Narrative Section: Requirement & Solution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
        {requirement && (
          <Card className="border-border">
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <HardHat className="size-5 text-primary" />
                <Badge variant="outline" className="text-xs">Specification</Badge>
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                The requirement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                {requirement}
              </p>
            </CardContent>
          </Card>
        )}

        {solution && (
          <Card className="border-border bg-card">
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="size-5 text-primary" />
                <Badge variant="outline" className="text-xs">Execution</Badge>
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                BMF solution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                {solution}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="mb-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold font-default text-foreground">Project gallery</h2>
              <p className="text-sm text-muted-foreground mt-1">Site photos and completed access structures</p>
            </div>
          </div>

          {galleryImages.length > 1 ? (
            <Carousel className="w-full" opts={{ align: "start", loop: true }}>
              <div className="relative">
                <CarouselContent>
                  {galleryImages.map((image, idx) => (
                    <CarouselItem key={idx} data-index={idx} className="sm:basis-1/2 lg:basis-1/3">
                      <div className="rounded-xl overflow-hidden border border-border bg-muted aspect-4/3 relative group">
                        <img
                          src={image.src}
                          data-wvc-srcset={image.srcset}
                          data-wvc-sizes={image.sizes}
                          alt={image.alt}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="flex justify-end gap-2 mt-4">
                  <CarouselPrevious className="static translate-y-0" />
                  <CarouselNext className="static translate-y-0" />
                </div>
              </div>
            </Carousel>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, idx) => (
                <div key={idx} data-index={idx} className="rounded-xl overflow-hidden border border-border bg-muted aspect-4/3">
                  <img
                    src={image.src}
                    data-wvc-srcset={image.srcset}
                    data-wvc-sizes={image.sizes}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Related Services Card */}
      <div className="mb-14">
        <Card className="border-border bg-accent/40">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <Badge variant="secondary" className="mb-2 text-xs">Relevant expertise</Badge>
              <CardTitle className="text-xl font-bold font-default text-card-foreground">
                Looking for {service ? `${service}` : "specialist scaffolding"} for your site?
              </CardTitle>
              <CardDescription className="text-muted-foreground mt-1">
                Explore our full technical scope, safety compliance, and bespoke access designs.
              </CardDescription>
            </div>
            <Button asChild variant="outline" className="shrink-0">
              <Link to={relatedServiceLink}>
                View service details
                <ExternalLink className="size-4 ml-1.5" />
              </Link>
            </Button>
          </CardHeader>
        </Card>
      </div>

      <Separator className="my-10" />

      {/* Call to Action Section */}
      <section className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 text-center relative overflow-hidden shadow-md">
        <div className="max-w-2xl mx-auto space-y-4">
          <Badge variant="outline" className="border-secondary-foreground/20 text-secondary-foreground">
            Get your estimate
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight">
            Ready to discuss your scaffolding requirements?
          </h2>
          <p className="text-secondary-foreground/80 text-sm sm:text-base leading-relaxed">
            Contact BMF Scaffolding Ltd for safety-led access solutions, site audits, and fixed quotations across Berkshire, Surrey, Hampshire, and London.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage">
                Request a quote
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10">
              <a href="tel:0800000000">
                <Phone className="size-4 mr-2" />
                Call our team
              </a>
            </Button>
          </div>
          <div className="pt-4">
            <Link
              to="/ProjectsCaseStudiesPage"
              className="inline-flex items-center text-xs sm:text-sm text-secondary-foreground/70 hover:text-secondary-foreground underline underline-offset-4"
            >
              <ArrowLeft className="size-3.5 mr-1" />
              Return to all case studies & projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function SingleProjectTemplateSection() {
  const currentWPQueryParams =
    typeof wvcClient !== "undefined" && wvcClient?.getCurrentWPQueryParams
      ? wvcClient.getCurrentWPQueryParams()
      : { post_type: "project" };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-project-template-section"
      data-nav="light"
      className="bg-background text-foreground min-h-screen"
    >
      <PostsProvider wp_query={wp_query}>
        <SingleProjectConsumer />
      </PostsProvider>
    </section>
  );
}